/**
 * useLiveDataSources.ts
 *
 * Live-querying hook for data sources in a workspace.
 *  Re-renders automatically when any data source changes in IndexedDB.
 *
 * Replaces: useBoard().dataSources from boardContext
 *
 * Usage:
 *   const { dataSources, getDataSource, isLoading } = useLiveDataSources(workspaceId)
 */

"use client";

import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import { appDB } from "../db/client";
import type { DataSourceRecord } from "../db/schema";

interface UseLiveDataSourcesReturn {
  // All data sources for this workspace — same as boardContext.dataSources (as array)
  dataSources: DataSourceRecord[];

  // Quick lookup by ID — same as boardContext.getDataSource()
  getDataSource: (dataSourceId: string) => DataSourceRecord | undefined;

  // Whether still loading from IDB on first mount
  isLoading: boolean;
}

/**
 * Returns all data sources for a workspace, live-updating from IndexedDB.
 */
export function useLiveDataSources(workspaceId: string): UseLiveDataSourcesReturn {
  const dataSources = useLiveQuery(
    () =>
      appDB.dataSources
        .where("workspaceId")
        .equals(workspaceId)
        .toArray(),
    [workspaceId]
  );

  const dataSourceMap = useMemo(() => {
    const map = new Map<string, DataSourceRecord>();
    if (!dataSources) return map;
    for (const ds of dataSources) {
      map.set(ds._id, ds);
    }
    return map;
  }, [dataSources]);

  const getDataSource = (dataSourceId: string): DataSourceRecord | undefined =>
    dataSourceMap.get(dataSourceId);

  return {
    dataSources: dataSources ?? [],
    getDataSource,
    isLoading: dataSources === undefined,
  };
}
